import { ReactElement, useState, FocusEvent, ChangeEvent } from 'react'
import { Field, FieldInputProps, useField } from '@util/Formik'
import { PasswordVisibilityAdornment } from '@components/PasswordVisibilityAdornment'
import { TextField, TextFieldProps } from '@internations/ds'

type StringKeys<T> = Extract<keyof T, string>

const TextFieldWithProps = <T extends object>(props: FieldInputProps<keyof T> & { fullWidth?: boolean }) => (
    <TextField fullWidth {...props} />
)

type TextInputProps<T extends object> = Omit<TextFieldProps, 'name' | 'data-testid'> & {
    name: StringKeys<T>
    'data-testid'?: string
    readOnly?: boolean
    maxLength?: number
}

export const FormikTextInput = <T extends object>({
    name,
    label,
    type,
    maxLength,
    ...props
}: TextInputProps<T>): ReactElement => {
    const [field, meta] = useField<string>(name)
    const [isEditing, setIsEditing] = useState(false)
    const [showPassword, setShowPassword] = useState(false)

    const hasError = Boolean(meta.error && meta.touched)
    const isPasswordField = type === 'password'
    const inputType = isPasswordField && showPassword ? 'text' : type

    return (
        <Field
            type={inputType ? inputType : 'text'}
            name={name}
            label={label}
            as={TextFieldWithProps}
            error={!isEditing && hasError}
            helperText={!isEditing && hasError ? meta.error : null}
            slotProps={{
                htmlInput: {
                    ...props,
                    maxLength,
                },
                input: {
                    endAdornment: isPasswordField && (
                        <PasswordVisibilityAdornment showPassword={showPassword} setShowPassword={setShowPassword} />
                    ),
                },
                inputLabel: {
                    shrink: Boolean(field.value) || undefined,
                },
            }}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                setIsEditing(true)
                field.onChange(e)
            }}
            onBlur={(e: FocusEvent<HTMLInputElement>) => {
                setIsEditing(false)
                field.onBlur(e)
            }}
            onFocus={() => {
                setIsEditing(true)
            }}
        />
    )
}
